import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ThemeMode, LMSPreferences } from '@/features/auth/domain/value-objects/UserPreferences';

/**
 * Preferences Store State Interface
 */
interface PreferencesState {
  // Data
  theme: ThemeMode;
  lms: LMSPreferences;
  userId: string | null;
  lastSynced: number | null;
  isSyncing: boolean;
  syncError: string | null;

  // Actions
  setTheme: (theme: ThemeMode) => void;
  setLMSPreferences: (lms: Partial<LMSPreferences>) => void;
  setUserId: (userId: string | null) => void;
  setSyncing: (isSyncing: boolean) => void;
  setSyncError: (error: string | null) => void;
  setLastSynced: (timestamp: number) => void;
  reset: () => void;
}

/**
 * Initial state for preferences store
 */
const initialState = {
  theme: 'light' as ThemeMode,
  lms: {
    playback_speed: 1.0,
    autoplay_next_lesson: true,
    auto_resume_videos: false,
  },
  userId: null,
  lastSynced: null,
  isSyncing: false,
  syncError: null,
};

/**
 * Preferences Store
 *
 * Manages user preferences client-side with local persistence
 * for instant UI updates. Syncs with database in background.
 *
 * Features:
 * - Persisted to localStorage for instant theme on page load
 * - Syncs with database for cross-device consistency
 * - Optimistic updates for responsive UI
 *
 * Usage:
 * ```typescript
 * const { theme, setTheme } = usePreferencesStore();
 * ```
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      ...initialState,

      setTheme: (theme) => set({ theme }),
      setLMSPreferences: (lmsUpdate) => set((state) => ({
        lms: { ...state.lms, ...lmsUpdate },
      })),
      setUserId: (userId) => set({ userId }),
      setSyncing: (isSyncing) => set({ isSyncing }),
      setSyncError: (error) => set({ syncError: error }),
      setLastSynced: (timestamp) => set({ lastSynced: timestamp }),
      reset: () => set(initialState),
    }),
    {
      name: 'user-preferences',
      partialize: (state) => ({
        theme: state.theme,
        lms: state.lms,
        userId: state.userId,
      }),
    }
  )
);

/**
 * Selector for resolved theme (handles 'system' preference)
 */
export const selectResolvedTheme = (state: PreferencesState): 'light' | 'dark' => {
  if (state.theme === 'system') {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }
  return state.theme;
};

/**
 * Selectors for optimized component rendering
 */
export const preferencesSelectors = {
  selectTheme: (state: PreferencesState) => state.theme,
  selectLMSPreferences: (state: PreferencesState) => state.lms,
  selectPlaybackSpeed: (state: PreferencesState) => state.lms.playback_speed ?? 1.0,
  selectAutoplayEnabled: (state: PreferencesState) => state.lms.autoplay_next_lesson ?? true,
  selectAutoResumeEnabled: (state: PreferencesState) => state.lms.auto_resume_videos ?? false,
  selectIsSyncing: (state: PreferencesState) => state.isSyncing,
  selectSyncError: (state: PreferencesState) => state.syncError,
  selectUserId: (state: PreferencesState) => state.userId,
};
